import {
  TESTED_ASSET_MAX_BY_TYPE,
  isAssetFamily,
  isYoutubeUrl,
  unreadableVideoHost,
  type TestedAssetType,
} from "@digital-twins/shared-types";

import type { TFunction } from "i18next";

import { chatT } from "@/i18n";
import type enApp from "@/i18n/locales/en/app.json";

import { familyNounInLanguage } from "./questionnaireIssueCopy";

/**
 * The words of the cards and lines the builder composes in the thread itself
 * (the material card, the new-assets card, the attach receipt and the lines
 * about links and files): in the language the chat speaks, which is the
 * draft's `conversationLanguage` and may be any chat language whatever the
 * interface language is. Every function takes that language (`chatLanguage()`
 * from a handler, a message's own `language` at render) and looks its line up
 * through `chatT`, the one lookup of every chat line code writes; the
 * component keeps its interface `t` for the composer, buttons and dialogs.
 *
 * Cache scope: one-shot display copy; not an LLM prompt.
 */

type AddAsFamily = keyof typeof enApp.questionnaire.materialCard.addAs;

/** A family's noun, in the middle of a sentence; the fallback noun for a mixed set. */
export function assetNounLine(
  family: string | null,
  count: number,
  lng: string | undefined,
): string {
  const form = count === 1 ? "singular" : "plural";
  return family !== null && isAssetFamily(family)
    ? familyNounInLanguage(family, form, lng)
    : chatT(lng)(`questionnaire.assetsFallback.${form}`).toLocaleLowerCase(lng);
}

/** "3 is the limit for ads." */
export function typeCapLine(limited: TestedAssetType, lng: string | undefined): string {
  return chatT(lng)("questionnaire.typeCapReached", {
    count: TESTED_ASSET_MAX_BY_TYPE[limited],
    type: familyNounInLanguage(limited, "plural", lng),
  });
}

/**
 * More files were given than a family has room for: its limit, and the ask
 * that the user names which to add. The study never takes a subset the user
 * did not pick.
 */
export function whichToAddLine(limited: TestedAssetType, lng: string | undefined): string {
  return `${typeCapLine(limited, lng)} ${chatT(lng)("questionnaire.materialCard.whichToAdd")}`;
}

/** What the card says when nothing can take the material and the study's words make the item. */
export function describeItLine(lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.describeIt");
}

export type MaterialGiven = "video" | "image" | "file";

/** The card's question: what was given, which one to pick, what the family can take. */
export function materialCardQuestion(
  lng: string | undefined,
  card: {
    given: MaterialGiven;
    count: number;
    /** More were given than the items take: the user picks one. */
    pick: "none" | "one" | "each";
    /** The one item a pick names, with `pick: "one"`. */
    pickItem: string | null;
  },
): string {
  const t = chatT(lng);
  return [
    t("questionnaire.materialCard.ask", {
      given: t(`questionnaire.materialCard.given.${card.given}`, { count: card.count }),
    }),
    card.pick === "none"
      ? ""
      : card.pick === "one"
        ? t("questionnaire.materialCard.pickOne", { item: card.pickItem })
        : t("questionnaire.materialCard.pickEach"),
  ]
    .filter(Boolean)
    .join(" ");
}

/** "Use <file>", or "Use <file> for "<item>"" when the pick names the item. */
export function useFileLabel(
  file: string,
  item: string | null,
  lng: string | undefined,
): string {
  const t = chatT(lng);
  return item !== null
    ? t("questionnaire.materialCard.useFor", { file, item })
    : t("questionnaire.materialCard.use", { file });
}

export function addAsLabel(family: string, lng: string | undefined): string {
  return chatT(lng)(`questionnaire.materialCard.addAs.${family as AddAsFamily}`);
}

export function addToItemLabel(item: string, lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.addTo", { item });
}

export function addToEachLabel(lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.addToEach");
}

export function leaveOutLabel(lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.leaveOut");
}

/** The answer to "No, leave it out". */
export function leftOutLine(lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.leftOut");
}

/** What a pick among several files leaves out, named. */
export function pickLeftOutLine(others: ReadonlyArray<string>, lng: string | undefined): string {
  return chatT(lng)("questionnaire.attachCard.leftOut", {
    count: others.length,
    files: others.join(", "),
  });
}

/** "<file> → <item>": which file went on which item. */
export function attachReceiptLine(
  line: { file?: string | null; item: string },
  lng: string | undefined,
): string {
  const t = chatT(lng);
  return line.file
    ? t("questionnaire.attachCard.receiptLine", { file: line.file, item: line.item })
    : t("questionnaire.attachCard.receiptLineNoName", { item: line.item });
}

export function alreadyHasFileLine(item: string, lng: string | undefined): string {
  return chatT(lng)("questionnaire.materialCard.alreadyHasFile", { item });
}

/** The new-assets card: what was added, and its two answers. */
export function newAssetsCardCopy(
  lng: string | undefined,
  assets: { family: string | null; count: number },
): { content: string; updateLabel: string; addMyselfLabel: string } {
  const t = chatT(lng);
  return {
    content: t("questionnaire.newAssetsCard.prompt", {
      count: assets.count,
      noun: assetNounLine(assets.family, assets.count, lng),
    }),
    updateLabel: t("questionnaire.newAssetsCard.update"),
    addMyselfLabel: t("questionnaire.newAssetsCard.addMyself", { count: assets.count }),
  };
}

/** A link or file the family already holds: nothing was added. */
export function alreadyAttachedLine(family: string, lng: string | undefined): string {
  return chatT(lng)("questionnaire.alreadyAttached", {
    noun: assetNounLine(family, 1, lng),
  });
}

/** Added to the study with no questions yet: the intake goes back to the objective. */
export function addedBackToObjectiveLine(
  family: string,
  count: number,
  lng: string | undefined,
): string {
  return chatT(lng)("questionnaire.addedBackToObjective", {
    count,
    noun: assetNounLine(family, count, lng),
  });
}

/**
 * A link to a video host with no frames to read, refused by name, with what to
 * do instead (the one wording of a refused link: the local refusal and a
 * Save's `collected_link_refused` effect both say it).
 */
export function refusedLinkLine(url: string, t: TFunction<"app">): string {
  if (isYoutubeUrl(url)) return t("questionnaire.linkRefused.youtube", { url });
  const host = unreadableVideoHost(url);
  return host === null ? url : t("questionnaire.linkRefused.host", { url, host });
}
